// Server/Server/client/src/admin/Organization/PurgeOrganizationModal.tsx
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import OrganizationAPI from "./API/OrganizationAPI";
import { PurgeImpactedOrg } from "./API/purgeTypes";
import { Button } from "src/components/buttons";

interface IProps {
  isOpen: boolean;
  organizationId: number;
  organizationName: string;
  onClose: () => void;
}

// Compact, centered dialog (react-modal's default content fills most of the viewport).
const modalStyle = {
  overlay: { backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 } as React.CSSProperties,
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "480px",
    maxHeight: "80vh",
    overflow: "auto",
    padding: "1.5rem",
  } as React.CSSProperties,
};

const PurgeOrganizationModal: React.FC<IProps> = ({ isOpen, organizationId, organizationName, onClose }) => {
  const [impacted, setImpacted] = useState<PurgeImpactedOrg[]>([]);
  const [nameInput, setNameInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setNameInput("");
    setImpacted([]);
    OrganizationAPI.getPurgeImpactedOrgs(organizationId)
      .then(setImpacted)
      .catch(() => toast.error("Could not load impacted organizations."));
  }, [isOpen, organizationId]);

  const canSubmit = nameInput === organizationName && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await OrganizationAPI.enqueuePurge(organizationId);
      if (result.alreadyQueued) {
        toast.warning(`${organizationName} is already queued for deletion. Please wait.`);
      } else {
        // Stays up until the user dismisses it (deletion runs asynchronously afterward).
        toast.success(`Purge queued for ${organizationName}.`, { autoClose: false });
      }
      onClose();
    } catch {
      toast.error("Failed to queue purge.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false} style={modalStyle}>
      <h3>Purge organization</h3>
      <p>
        <strong>This permanently deletes "{organizationName}" and all its data. This cannot be undone.</strong>
      </p>

      {impacted.length > 0 && (
        <div data-testid="impacted-orgs" style={{ marginBottom: "1rem" }}>
          <p>
            This organization's users created records in other organizations. The following
            sessions will be re-mapped to a "Deleted User" placeholder in each organization:
          </p>
          <ul style={{ marginTop: "0.25rem", maxHeight: "10rem", overflowY: "auto", paddingLeft: "1.25rem" }}>
            {impacted.map((o) => (
              <li key={o.organizationId} data-testid={`impacted-org-${o.organizationId}`}>
                <strong>{o.organizationName}</strong> — {o.impactedSessionCount}{" "}
                {o.impactedSessionCount === 1 ? "session" : "sessions"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <label style={{ display: "block" }}>Type the organization name to confirm:</label>
      <input data-testid="purge-name-input" value={nameInput}
             onChange={(e) => setNameInput(e.target.value)}
             style={{ display: "block", marginTop: "0.25rem", marginBottom: "1rem" }} />

      <div style={{ marginTop: "1.5rem" }}>
        <Button className="btn-secondary" onClick={onClose}>Cancel</Button>
        <Button className="btn-primary" data-testid="purge-submit" style={{ marginLeft: "0.5rem" }}
                disabled={!canSubmit} onClick={submit}>Purge organization</Button>
      </div>
    </Modal>
  );
};

export default PurgeOrganizationModal;
